import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { BrowserAgent } from '@newrelic/browser-agent'
import * as Sentry from '@sentry/react'
import App from './App'
import { sampleRate } from './lib/telemetry'
import { installStaleChunkReload } from './lib/staleChunk'
import ClerkProviderWithNavigate from './auth/ClerkProviderWithNavigate'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from '@comforthub/design-system'
import './index.css'

/** W3C trace headers (baggage/traceparent) break Clerk CORS preflight. */
const API_TRACE_TARGETS: Array<string | RegExp> = [
  /^\//,
  /api\.comforthub\.ee/,
  /api-dev\.comforthub\.ee/,
]

const isLocalDev =
  import.meta.env.DEV ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'

/**
 * HTTP status behind a captured AxiosError, or undefined if this event is not one.
 *
 * Axios puts the status on the original error, which Sentry keeps on the event's
 * hint/originalException rather than the serialised exception, so read it from
 * `extra`/`contexts` as well as the message text. Matching the message is the
 * fallback that works regardless of how the rejection reached Sentry.
 */
function axiosStatusOf(event: Sentry.ErrorEvent): number | undefined {
  const response = (event.contexts?.response ?? {}) as { status_code?: number }
  if (typeof response.status_code === 'number') {
    return response.status_code
  }
  const values = event.exception?.values ?? []
  for (const value of values) {
    if (value.type !== 'AxiosError') continue
    const match = /status code (\d{3})/.exec(value.value ?? '')
    if (match) return Number(match[1])
  }
  return undefined
}

const nrLicenseKey = import.meta.env.VITE_NEW_RELIC_LICENSE_KEY
const nrAppId = import.meta.env.VITE_NEW_RELIC_APPLICATION_ID

if (!isLocalDev && nrLicenseKey && nrAppId) {
  try {
    new BrowserAgent({
      info: {
        beacon: 'bam.eu01.nr-data.net',
        errorBeacon: 'bam.eu01.nr-data.net',
        licenseKey: nrLicenseKey,
        applicationID: nrAppId,
        sa: 1
      },
      init: {
        distributed_tracing: {
          enabled: true,
          cors_use_newrelic_header: false,
          cors_use_tracecontext_headers: false,
          allowed_origins: ['https://api-dev.comforthub.ee', 'https://api.comforthub.ee']
        }
      }
    })
  } catch (e) {
    console.error('Failed to initialize New Relic Browser Agent:', e)
  }
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE
const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT

if (!isLocalDev && sentryDsn) {
  try {
    Sentry.init({
      dsn: sentryDsn,
      release: sentryRelease,
      environment: sentryEnv || 'development',
      integrations: [
        Sentry.browserTracingIntegration(),
        // 'warn' used to be here, which is how vendor deprecation notices (Clerk's
        // redirectUrl, dev-keys) turned into Sentry Issues. Warnings belong in Logs,
        // not the issue stream.
        Sentry.captureConsoleIntegration({
          levels: ['error'],
        }),
      ],
      // Application logs — emitted only via src/lib/telemetry.ts, never scraped
      // from the console. See that module for why.
      enableLogs: true,
      tracesSampleRate: sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0),
      tracePropagationTargets: API_TRACE_TARGETS,
      // A 401/403 from the API is an auth state, not a fault: it happens on every
      // page load that renders before Clerk has resolved a token, and on any route
      // the signed-in user is not entitled to. Both are handled — the app redirects
      // or shows a permission notice — but the rejected promise still reached
      // Sentry's global handler and filed a fatal Issue each time. The failure is
      // still visible: api.ts leaves a breadcrumb and a log line for every one.
      beforeSend(event) {
        const status = axiosStatusOf(event)
        if (status === 401 || status === 403) {
          return null
        }
        return event
      },
    })
  } catch (e) {
    console.error('Failed to initialize Sentry:', e)
  }
}

installStaleChunkReload()

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

/**
 * A data router, so a page can ask before a navigation drops its unsaved changes (useBlocker,
 * see InventoryItemPage). One route matches every path and holds the whole app as before;
 * App's own <Routes> still decide every page.
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <ClerkProviderWithNavigate>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ClerkProviderWithNavigate>
    ),
  },
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
)
